import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import {
    Divider,
    IconButton,
    ListItemButton,
    ListItemButtonProps,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import { SxProps, Theme, styled, useTheme } from '@mui/material/styles';
import React, { useCallback } from 'react';
import { Link, redirect } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useAuth, usePathname } from '../../hooks';
import { appActions } from '../../store/app-slice';
import { ToolTip } from '../controls';
import { Drawer, DrawerHeader, List, colorStyles } from '../styled/Sidebar.styled';

import type { LinkItem } from '../../types';
import { useLinkItemsAdmin, useLinkItemsCompanyOwner } from 'constants/link-items';
import { APP_ROLES_NAMES } from 'constants/app-roles';
import { useTranslation } from 'react-i18next';

interface StyledListItemButtonProps extends ListItemButtonProps {
    selected?: boolean;
    to: string;
    icon: React.ReactNode;
    name: string;
    colorStyles: SxProps<Theme>;
    sx?: SxProps<Theme>;
    disabled?: boolean;
}

export interface AppLinkItem {
    id: number;
    name: string;
    description?: string;
    to: string;
    icon: React.ReactNode;
    disabled?: boolean;
}

const StyledListItemButton = styled(
    ({ selected, to, icon, name, colorStyles, disabled, ...props }: StyledListItemButtonProps) => (
        <ListItemButton
            component={Link}
            to={disabled ? '#' : to}
            selected={selected}
            {...props}
            sx={{
                pointerEvents: disabled ? 'none' : 'auto',
                opacity: disabled ? 0.5 : 1,
                ...props.sx,
            }}
        >
            <ListItemIcon
                sx={{
                    minWidth: 'unset', // CENTERING ICONS
                    justifyContent: 'center', // CENTERING ICONS
                    width: '50px', // CENTERING ICONS
                    ...colorStyles,
                }}
            >
                {icon}
            </ListItemIcon>
            <ListItemText primary={name} sx={colorStyles} />
        </ListItemButton>
    ),
)(({ theme, selected }) => ({
    borderRadius: '12px',
    marginLeft: '10px',
    marginRight: '5px',
    paddingInline: 'unset', // CENTERING ICONS
    gap: '6px', // CENTERING ICONS
    position: 'relative',
    '&::before': {
        content: '""',
        width: '4px',
        height: '100%',
        backgroundColor: selected ? theme.palette.primary.main : 'transparent',
        position: 'absolute',
        left: -10,
        top: 0,
        bottom: 0,
        borderRadius: '0 5px 5px 0',
    },
    '& .MuiTypography-root': {
        fontWeight: 500, // Make text bold
    },
    '&:hover': {
        backgroundColor: 'transparent', // Remove the default hover background
        '& .MuiListItemText-root': {
            color: theme.palette.primary.main, // Custom text color on hover
        },
    },
    '&.Mui-selected': {
        '& .MuiListItemText-root': {
            color: theme.palette.primary.main, // Custom text color for selected
        },
    },
}));

/**
 * Sidebar
 */
const Sidebar = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const theme = useTheme();
    const pathname = usePathname();

    const drawerOpen: boolean = useAppSelector((state) => state.app.drawerOpen);

    const selectedItem = useCallback<(to: string) => boolean>((to: string) => pathname.startsWith(to), [pathname]);

    const handleDrawerClose = () => {
        dispatch(appActions.setDrawerOpen(false));
    };

    const { role } = useAuth();
    const linkItemsOwner = useLinkItemsCompanyOwner();
    const linkItemsAdmin = useLinkItemsAdmin();
    if (!role) redirect('/login');

    const trimAfter5thSlash = useCallback((path: string) => {
        const parts = path.split('/');
        if (parts.length <= 4) return path;
        return parts.slice(0, 4).join('/');
    }, []);

    const linkItems = useCallback(
        (role: string): LinkItem[] => {
            switch (role) {
                case APP_ROLES_NAMES.ADMINISTRATOR:
                    return linkItemsAdmin;
                case APP_ROLES_NAMES.COMPANY_OWNER:
                    return linkItemsOwner;
                default:
                    return [];
            }
        },
        [linkItemsAdmin, linkItemsOwner],
    );

    return (
        <React.Fragment>
            <Drawer variant="permanent" open={drawerOpen}>
                <DrawerHeader sx={{ borderBottom: '1px solid #f2f7fb', marginBottom: '10px' }}>
                    {drawerOpen && (
                        <Stack direction={'row'} alignItems={'center'}>
                            <img height={60} width={60} src={'/logo.png'} alt="Order Wave Logo" />
                            <Typography fontSize={14} variant="h6">
                                ORDER WAVE
                            </Typography>
                        </Stack>
                    )}
                    <IconButton onClick={handleDrawerClose} sx={colorStyles}>
                        {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                    </IconButton>
                </DrawerHeader>

                <List component="nav" disablePadding>
                    {linkItems(role!).map(({ id, name, to, icon, disabled }: AppLinkItem, index) => {
                        const trimmed = trimAfter5thSlash(to);
                        const isSelected = selectedItem(trimmed);

                        const appliedColor = !isSelected ? colorStyles : { color: theme.palette.primary.main };

                        const sharedProps = {
                            to,
                            icon,
                            name,
                            selected: isSelected,
                            colorStyles: appliedColor,
                            disabled,
                            sx: {
                                '&:hover .MuiListItemText-root, &:hover .MuiListItemIcon-root': {
                                    color: theme.palette.primary.main,
                                },
                            },
                        };

                        return (
                            <React.Fragment key={id}>
                                {drawerOpen ? (
                                    disabled ? (
                                        <ToolTip title={t('COMMON.MISSING_PLACE')} placement="top">
                                            <div>
                                                <StyledListItemButton {...sharedProps} />
                                            </div>
                                        </ToolTip>
                                    ) : (
                                        <StyledListItemButton {...sharedProps} />
                                    )
                                ) : (
                                    <ToolTip title={disabled ? `${t('COMMON.MISSING_PLACE')}` : name} placement="right">
                                        <StyledListItemButton {...sharedProps} />
                                    </ToolTip>
                                )}
                                {role === APP_ROLES_NAMES.ADMINISTRATOR && index === 4 && (
                                    <Divider sx={{ my: 3, backgroundColor: '#f2f7fb' }} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </List>
            </Drawer>
        </React.Fragment>
    );
};

export default Sidebar;
